/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { cn } from "@/lib/utils";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    variant?: "default" | "success" | "warning" | "danger";
    size?: "sm" | "md" | "lg";
    style?: React.CSSProperties;
  }
>(
  (
    {
      className,
      sideOffset = 4,
      variant = "default",
      size = "md",
      style,
      ...props
    },
    ref,
  ) => {
    // Semantic variant colors using Tailwind classes
    const variantClasses = {
      default: "bg-surface-container-highest text-popover-foreground",
      success: "bg-success text-success-foreground",
      warning: "bg-warning text-warning-foreground",
      danger: "bg-destructive text-destructive-foreground",
    };

    const sizeClasses = {
      sm: "text-xs px-2 py-1",
      md: "text-sm px-3 py-2",
      lg: "text-base px-4 py-3",
    };

    return (
      <TooltipPrimitive.Content
        ref={ref}
        sideOffset={sideOffset}
        className={cn(
          "z-50 overflow-hidden rounded-md border px-3 py-1.5 text-sm shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
          variantClasses[variant],
          sizeClasses[size],
          className,
        )}
        style={style}
        {...props}
      />
    );
  },
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

// Enhanced tooltip with rich content
interface EnhancedTooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  title?: string;
  description?: string;
  variant?: "default" | "success" | "warning" | "danger";
  size?: "sm" | "md" | "lg";
  delayDuration?: number;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  className?: string;
}

export function EnhancedTooltip({
  children,
  content,
  title,
  description,
  variant = "default",
  size = "md",
  delayDuration = 500,
  side = "top",
  align = "center",
  className,
}: EnhancedTooltipProps) {
  return (
    <Tooltip delayDuration={delayDuration}>
      <TooltipTrigger asChild className={className}>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side={side}
        align={align}
        variant={variant}
        size={size}
        className="max-w-xs"
      >
        {typeof content === "string" ? (
          <div className="space-y-1">
            {title ? <div className="font-medium">{title}</div> : null}
            <div>{content}</div>
            {description ? (
              <div className="text-xs opacity-80">{description}</div>
            ) : null}
          </div>
        ) : (
          content
        )}
      </TooltipContent>
    </Tooltip>
  );
}

// Info tooltip component
interface InfoTooltipProps {
  children: React.ReactNode;
  info: string;
  title?: string;
  className?: string;
}

export function InfoTooltip({
  children,
  info,
  title,
  className,
}: InfoTooltipProps) {
  return (
    <EnhancedTooltip
      content={info}
      {...(title !== undefined ? { title } : {})}
      variant="default"
      size="sm"
      {...(className ? { className } : {})}
    >
      {children}
    </EnhancedTooltip>
  );
}

// Help tooltip component
interface HelpTooltipProps {
  children: React.ReactNode;
  help: string;
  className?: string;
}

export function HelpTooltip({ children, help, className }: HelpTooltipProps) {
  return (
    <EnhancedTooltip
      content={help}
      variant="default"
      size="sm"
      className={cn("cursor-help", className)}
    >
      {children}
    </EnhancedTooltip>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
